/** Small presentational pieces shared by the Bots pages. */
import { useEffect, useRef, useState } from 'react';
import type { CSSProperties, ReactNode } from 'react';
import type { BotEnv, BotHealth, BotState } from '../../lib/bots';

export const mono: CSSProperties = { fontFamily: 'var(--tk-mono)', fontVariantNumeric: 'tabular-nums' };

export const fmt = (n: number | null | undefined, d = 2) =>
  n === null || n === undefined || !Number.isFinite(n) ? '—' : n.toLocaleString('en-PK', { minimumFractionDigits: d, maximumFractionDigits: d });
export const fmtInt = (n: number | null | undefined) => (n === null || n === undefined || !Number.isFinite(n) ? '—' : Math.round(n).toLocaleString('en-PK'));
export const signed = (n: number | null | undefined, d = 2) =>
  n === null || n === undefined || !Number.isFinite(n) ? '—' : `${n > 0 ? '+' : n < 0 ? '−' : ''}${fmt(Math.abs(n), d)}`;
export const pnlColor = (n: number | null | undefined) => (!n ? 'var(--tk-ink-2)' : n > 0 ? 'var(--tk-up)' : 'var(--tk-down)');

const PKT: Intl.DateTimeFormatOptions = { timeZone: 'Asia/Karachi', hour12: false };
export const pktTime = (iso: string | number | null | undefined) =>
  iso ? new Date(iso).toLocaleTimeString('en-GB', { ...PKT, hour: '2-digit', minute: '2-digit', second: '2-digit' }) : '—';
export const pktDateTime = (iso: string | number | null | undefined) =>
  iso ? new Date(iso).toLocaleString('en-GB', { ...PKT, day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit', second: '2-digit' }) : '—';

const STATE_TONE: Record<BotState, { color: string; label: string }> = {
  STARTING: { color: 'var(--tk-warn)', label: 'waiting' },
  ACQUIRING: { color: 'var(--tk-up)', label: 'acquiring' },
  RUNNING: { color: 'var(--tk-up)', label: 'running' },
  STOPPING: { color: 'var(--tk-warn)', label: 'stopping' },
  STOPPED: { color: 'var(--tk-ink-3)', label: 'stopped' },
  ERROR: { color: 'var(--tk-down)', label: 'error' },
  CASH_STARVED: { color: 'var(--tk-down)', label: 'cash starved' },
  INTERRUPTED: { color: 'var(--tk-down)', label: 'interrupted' },
};

export const Pill = ({ color, children, solid }: { color: string; children: ReactNode; solid?: boolean }) => (
  <span
    style={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: 6,
      padding: '3px 9px',
      borderRadius: 999,
      border: `1px solid ${solid ? color : 'var(--tk-line)'}`,
      fontSize: 10.5,
      fontWeight: 600,
      color,
      letterSpacing: '0.04em',
      textTransform: 'uppercase',
      whiteSpace: 'nowrap',
      ...mono,
    }}
  >
    <span style={{ width: 6, height: 6, borderRadius: 999, background: color, flex: 'none' }} />
    {children}
  </span>
);

export const StatePill = ({ state }: { state: BotState }) => <Pill color={STATE_TONE[state].color}>{STATE_TONE[state].label}</Pill>;

/**
 * RUNNING is not the same as working. Green only when the grid is actually
 * turning; anything else is amber and says what is holding it up, so a bot that
 * stops cycling is visible at a glance instead of three hours later.
 */
const HEALTH_TONE: Record<BotHealth, { label: string; why: string }> = {
  cycling: { label: 'cycling', why: 'Both legs are resting — a fill on either side re-quotes the other.' },
  'one-legged': { label: 'one-legged', why: 'A leg it should be quoting is missing. Nothing happens on that side until the leg it still has fills.' },
  capped: { label: 'capped', why: 'At the inventory cap: it can only sell from here, so there is no buy leg by design.' },
  'waiting-on-cancel': { label: 'waiting on cancel', why: 'A cancel was asked for and never confirmed. The broker may already have executed it.' },
};

export const HealthPill = ({ health }: { health: BotHealth | null }) =>
  health ? (
    <span title={HEALTH_TONE[health].why}>
      <Pill color={health === 'cycling' ? 'var(--tk-up)' : 'var(--tk-warn)'}>{HEALTH_TONE[health].label}</Pill>
    </span>
  ) : null;

/** LIVE is money: make it impossible to mistake for the sandbox. */
export const EnvPill = ({ env }: { env: BotEnv }) => (
  <Pill color={env === 'live' ? 'var(--tk-down)' : 'var(--tk-ink-3)'} solid={env === 'live'}>
    {env}
  </Pill>
);

export const Stat = ({ label, value, sub, color }: { label: string; value: ReactNode; sub?: ReactNode; color?: string }) => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
    <span style={{ fontSize: 10, color: 'var(--tk-ink-3)', letterSpacing: '0.06em', textTransform: 'uppercase', fontWeight: 600, ...mono }}>{label}</span>
    <span style={{ fontSize: 15, fontWeight: 600, color: color ?? 'var(--tk-ink)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', ...mono }}>{value}</span>
    {sub ? <span style={{ fontSize: 10.5, color: 'var(--tk-ink-3)', ...mono }}>{sub}</span> : null}
  </div>
);

export const SectionTitle = ({ children, right }: { children: ReactNode; right?: ReactNode }) => (
  <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 12, marginBottom: 12 }}>
    <h2 style={{ margin: 0, fontSize: 14, fontWeight: 700, letterSpacing: '-0.01em' }}>{children}</h2>
    {right ? <span style={{ fontSize: 11, color: 'var(--tk-ink-3)', ...mono }}>{right}</span> : null}
  </div>
);

export const Notice = ({ tone, children }: { tone: 'warn' | 'down' | 'muted' | 'up'; children: ReactNode }) => {
  const color = tone === 'warn' ? 'var(--tk-warn)' : tone === 'down' ? 'var(--tk-down)' : tone === 'up' ? 'var(--tk-up)' : 'var(--tk-ink-3)';
  return (
    <div style={{ borderLeft: `3px solid ${color}`, padding: '8px 12px', background: 'var(--tk-surface-2)', borderRadius: 'var(--tk-radius-xs)', fontSize: 12.5, color: 'var(--tk-ink-2)', lineHeight: 1.5 }}>
      {children}
    </div>
  );
};

export const inputStyle: CSSProperties = {
  width: '100%',
  boxSizing: 'border-box',
  padding: '9px 11px',
  border: '1px solid var(--tk-line)',
  borderRadius: 'var(--tk-radius-sm)',
  background: 'var(--tk-surface)',
  color: 'var(--tk-ink)',
  fontSize: 13,
  ...mono,
};

export const Field = ({ label, hint, children }: { label: string; hint?: ReactNode; children: ReactNode }) => (
  <label style={{ display: 'flex', flexDirection: 'column', gap: 5, minWidth: 0 }}>
    <span style={{ fontSize: 10.5, color: 'var(--tk-ink-3)', letterSpacing: '0.06em', textTransform: 'uppercase', fontWeight: 600, ...mono }}>{label}</span>
    {children}
    {hint ? <span style={{ fontSize: 11, color: 'var(--tk-ink-3)', lineHeight: 1.4 }}>{hint}</span> : null}
  </label>
);

export const Th = ({ children, left }: { children?: ReactNode; left?: boolean }) => (
  <th style={{ textAlign: left ? 'left' : 'right', padding: '8px 10px', fontSize: 10, fontWeight: 600, color: 'var(--tk-ink-3)', letterSpacing: '0.06em', textTransform: 'uppercase', borderBottom: '1px solid var(--tk-line)', whiteSpace: 'nowrap', ...mono }}>
    {children}
  </th>
);

export const Td = ({ children, left, color, bold, title }: { children?: ReactNode; left?: boolean; color?: string; bold?: boolean; title?: string }) => (
  <td title={title} style={{ textAlign: left ? 'left' : 'right', padding: '8px 10px', fontSize: 12.5, color: color ?? 'var(--tk-ink)', fontWeight: bold ? 600 : 400, borderBottom: '1px solid var(--tk-line-2)', whiteSpace: 'nowrap', ...mono }}>
    {children}
  </td>
);

export const TableWrap = ({ children, maxHeight }: { children: ReactNode; maxHeight?: number }) => (
  <div style={{ overflow: 'auto', maxHeight }}>
    <table style={{ width: '100%', borderCollapse: 'collapse' }}>{children}</table>
  </div>
);

export const Empty = ({ children }: { children: ReactNode }) => <div style={{ padding: '18px 4px', fontSize: 12.5, color: 'var(--tk-ink-3)' }}>{children}</div>;

export const ErrorLine = ({ children }: { children: ReactNode }) => (children ? <div style={{ fontSize: 12.5, color: 'var(--tk-down)', lineHeight: 1.5 }}>{children}</div> : null);

export const pageStyle = (isMobile: boolean): CSSProperties => ({
  maxWidth: 1440,
  margin: '0 auto',
  padding: isMobile ? '16px 16px 32px' : '24px 32px 48px',
  display: 'flex',
  flexDirection: 'column',
  gap: 16,
});

/** Run `load` now and every `ms` while the tab is visible. `load` owns its own state and errors. */
export const usePoll = (load: () => void | Promise<void>, ms: number) => {
  const ref = useRef(load);
  ref.current = load;
  useEffect(() => {
    void ref.current();
    const timer = setInterval(() => {
      if (document.visibilityState === 'visible') void ref.current();
    }, ms);
    return () => clearInterval(timer);
  }, [ms]);
};

// ---------------------------------------------------------------------------
// Building blocks for the redesigned Bots pages
// ---------------------------------------------------------------------------

/** Hover, focus and motion rules that inline styles cannot express. Render once per page. */
export const BotsStyles = () => (
  <style>{`
    .tkb-row { transition: background 0.15s var(--tk-ease); }
    .tkb-row:hover { background: var(--tk-surface-2); }
    .tkb-click { cursor: pointer; }
    .tkb-seg { border: none; background: transparent; color: var(--tk-ink-3); cursor: pointer; transition: all 0.18s var(--tk-ease); }
    .tkb-seg:hover { color: var(--tk-ink); }
    .tkb-seg[data-active='true'] { background: var(--tk-surface); color: var(--tk-ink); box-shadow: var(--tk-shadow-sm); }
    .tkb-ghost { border: 1px solid var(--tk-line); background: transparent; color: var(--tk-ink-2); cursor: pointer; transition: all 0.15s var(--tk-ease); }
    .tkb-ghost:hover { border-color: var(--tk-ink-3); color: var(--tk-ink); }
    .tkb-tile { transition: border-color 0.2s var(--tk-ease), transform 0.2s var(--tk-ease); }
    .tkb-tile:hover { border-color: var(--tk-ink-3); transform: translateY(-1px); }
    .tkb-pulse { animation: tk-pulse 1.8s ease-in-out infinite; }
    .tkb-fill { transition: width 0.6s var(--tk-ease); }
    /* The band gauge slides horizontally, the order ladder vertically. */
    .tkb-marker { transition: left 0.6s var(--tk-ease), top 0.6s var(--tk-ease); }
    @media (prefers-reduced-motion: reduce) { .tkb-pulse { animation: none; } .tkb-fill, .tkb-marker, .tkb-tile { transition: none; } }
  `}</style>
);

/** Small uppercase caption used above every figure, in the app's sans face. */
export const Caption = ({ children, title }: { children: ReactNode; title?: string }) => (
  <span title={title} style={{ fontSize: 11, color: 'var(--tk-ink-3)', fontWeight: 600, letterSpacing: '0.05em', textTransform: 'uppercase' }}>
    {children}
  </span>
);

/** A number that briefly tints green or red when it changes. */
export const FlashNumber = ({ value, text, style }: { value: number | null | undefined; text: string; style?: CSSProperties }) => {
  const prev = useRef(value);
  const [cls, setCls] = useState('');
  useEffect(() => {
    const before = prev.current;
    prev.current = value;
    if (before === null || before === undefined || value === null || value === undefined || before === value) return;
    setCls(value > before ? 'tk-flash-up' : 'tk-flash-down');
    const t = setTimeout(() => setCls(''), 900);
    return () => clearTimeout(t);
  }, [value]);
  return (
    <span className={cls} style={{ ...mono, ...style }}>
      {text}
    </span>
  );
};

/** One figure with its caption. `hint` goes in a tooltip, never on screen. */
export const Kpi = ({ label, value, color, hint, size = 18, children }: { label: string; value?: ReactNode; color?: string; hint?: string; size?: number; children?: ReactNode }) => (
  <div title={hint} style={{ display: 'flex', flexDirection: 'column', gap: 6, minWidth: 0 }}>
    <Caption>{label}</Caption>
    <span style={{ fontSize: size, fontWeight: 600, letterSpacing: '-0.02em', color: color ?? 'var(--tk-ink)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', ...mono }}>{value}</span>
    {children}
  </div>
);

/** Progress toward a limit. Turns amber then red as it fills. */
export const Meter = ({ label, used, limit, unit, hint }: { label: string; used: number; limit: number | null; unit?: string; hint?: string }) => {
  const ratio = limit && limit > 0 ? Math.min(1, used / limit) : 0;
  const tone = ratio >= 0.9 ? 'var(--tk-down)' : ratio >= 0.7 ? 'var(--tk-warn)' : 'var(--tk-up)';
  return (
    <div title={hint} style={{ display: 'flex', flexDirection: 'column', gap: 8, minWidth: 0 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 8 }}>
        <Caption>{label}</Caption>
        <span style={{ fontSize: 13, fontWeight: 600, ...mono }}>
          {fmtInt(used)}
          <span style={{ color: 'var(--tk-ink-3)', fontWeight: 500 }}>
            {' '}
            / {limit ? fmtInt(limit) : '∞'}
            {unit ? ` ${unit}` : ''}
          </span>
        </span>
      </div>
      <div style={{ height: 6, borderRadius: 999, background: 'var(--tk-surface-2)', overflow: 'hidden' }}>
        <div className="tkb-fill" style={{ width: `${ratio * 100}%`, height: '100%', borderRadius: 999, background: tone }} />
      </div>
    </div>
  );
};

/** Where a value sits inside a symmetric band, e.g. price inside the ±stop range. */
export const BandGauge = ({ label, pct, band, hint }: { label: string; pct: number | null; band: number; hint?: string }) => {
  const pos = pct === null ? 0.5 : Math.min(1, Math.max(0, (pct + band) / (2 * band)));
  const danger = pct !== null && Math.abs(pct) >= band * 0.8;
  return (
    <div title={hint} style={{ display: 'flex', flexDirection: 'column', gap: 8, minWidth: 0 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 8 }}>
        <Caption>{label}</Caption>
        <span style={{ fontSize: 13, fontWeight: 600, color: danger ? 'var(--tk-down)' : 'var(--tk-ink)', ...mono }}>
          {pct === null ? '—' : `${signed(pct)}%`}
          <span style={{ color: 'var(--tk-ink-3)', fontWeight: 500 }}> of ±{fmt(band, band % 1 ? 2 : 0)}%</span>
        </span>
      </div>
      <div style={{ position: 'relative', height: 6, borderRadius: 999, background: 'linear-gradient(90deg, var(--tk-down-soft), var(--tk-surface-2) 30%, var(--tk-surface-2) 70%, var(--tk-down-soft))' }}>
        <div style={{ position: 'absolute', left: '50%', top: -2, width: 1, height: 10, background: 'var(--tk-line)' }} />
        <div className="tkb-marker" style={{ position: 'absolute', left: `${pos * 100}%`, top: -3, width: 12, height: 12, marginLeft: -6, borderRadius: 999, background: danger ? 'var(--tk-down)' : 'var(--tk-accent)', border: '2px solid var(--tk-surface)' }} />
      </div>
    </div>
  );
};

/** Segmented control with optional count badges. */
export const Segmented = <T extends string>({ value, onChange, options }: { value: T; onChange: (v: T) => void; options: { id: T; label: string; count?: number }[] }) => (
  <div role="tablist" style={{ display: 'inline-flex', gap: 2, padding: 3, borderRadius: 999, background: 'var(--tk-surface-2)', border: '1px solid var(--tk-line-2)', maxWidth: '100%', overflowX: 'auto' }}>
    {options.map((o) => (
      <button
        key={o.id}
        role="tab"
        type="button"
        aria-selected={value === o.id}
        data-active={value === o.id}
        className="tkb-seg"
        onClick={() => onChange(o.id)}
        style={{ display: 'inline-flex', alignItems: 'center', gap: 7, padding: '7px 14px', borderRadius: 999, fontSize: 12.5, fontWeight: 600, fontFamily: 'var(--tk-sans)', whiteSpace: 'nowrap' }}
      >
        {o.label}
        {o.count !== undefined && (
          <span style={{ fontSize: 10.5, padding: '1px 6px', borderRadius: 999, background: value === o.id ? 'var(--tk-accent-soft)' : 'var(--tk-line-2)', color: value === o.id ? 'var(--tk-accent)' : 'var(--tk-ink-3)', ...mono }}>
            {o.count}
          </span>
        )}
      </button>
    ))}
  </div>
);

/** "Updated Ns ago" with a pulsing dot while polling is healthy. */
export const LiveBadge = ({ at, stale }: { at: number | null; stale?: boolean }) => {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  const secs = at ? Math.max(0, Math.round((now - at) / 1000)) : null;
  const bad = stale || secs === null || secs > 15;
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7, fontSize: 11.5, color: 'var(--tk-ink-3)', ...mono }}>
      <span className={bad ? '' : 'tkb-pulse'} style={{ width: 7, height: 7, borderRadius: 999, background: bad ? 'var(--tk-warn)' : 'var(--tk-up)' }} />
      {secs === null ? 'connecting' : secs < 2 ? 'live' : `${secs}s ago`}
    </span>
  );
};

/** Card header in the app's style: title left, quiet meta right. */
export const CardHead = ({ title, right }: { title: ReactNode; right?: ReactNode }) => (
  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginBottom: 18 }}>
    <h3 style={{ margin: 0, fontSize: 15, fontWeight: 600, letterSpacing: '-0.01em' }}>{title}</h3>
    {right ? <span style={{ fontSize: 11.5, color: 'var(--tk-ink-3)', ...mono }}>{right}</span> : null}
  </div>
);
